import type {
  CreateUserRequest,
  ListUsersRequest,
  ListUsersResponse,
  UpdatePasswordRequest,
  UpdateUserRequest,
  User,
  UserId,
} from "metabase-types/api";

import { Api } from "./api";
import {
  idTag,
  invalidateTags,
  listTag,
  provideUserListTags,
  provideUserTags,
} from "./tags";

export const userApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    listUsers: builder.query<ListUsersResponse, ListUsersRequest | void>({
      query: (params) => ({
        method: "GET",
        url: "/api/user",
        params,
      }),
      providesTags: (response) =>
        response ? provideUserListTags(response.data) : [],
    }),
    listUserRecipients: builder.query<ListUsersResponse, void>({
      query: () => ({
        method: "GET",
        url: "/api/user/recipients",
      }),
      providesTags: (response) =>
        response ? provideUserListTags(response.data) : [],
    }),
    getUser: builder.query<User, UserId>({
      query: (id) => ({
        method: "GET",
        url: `/api/user/${id}`,
      }),
      providesTags: (user) => (user ? provideUserTags(user) : []),
    }),
    getCurrentUser: builder.query<User, void>({
      query: () => ({
        method: "GET",
        url: "/api/user/current",
      }),
      providesTags: (user) => (user ? [idTag("current-user", user.id)] : []),
      // Don't garbage-collect the current user from the cache
      // since it's used in many places and we don't want to refetch it unnecessarily.
      keepUnusedDataFor: Infinity,
    }),
    createUser: builder.mutation<User, CreateUserRequest>({
      query: (body) => ({
        method: "POST",
        url: "/api/user",
        body,
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [
          listTag("user"),
          listTag("tenant"),
          listTag("permissions-group"),
        ]),
    }),
    updatePassword: builder.mutation<void, UpdatePasswordRequest>({
      query: ({ id, old_password, password }) => ({
        method: "PUT",
        url: `/api/user/${id}/password`,
        body: { old_password, password },
      }),
      invalidatesTags: (_, error, { id }) =>
        invalidateTags(error, [listTag("user"), idTag("user", id)]),
    }),
    deactivateUser: builder.mutation<void, UserId>({
      query: (id) => ({
        method: "DELETE",
        url: `/api/user/${id}`,
      }),
      invalidatesTags: (_, error, id) =>
        invalidateTags(error, [
          listTag("user"),
          idTag("user", id),
          listTag("permissions-group"),
        ]),
    }),
    reactivateUser: builder.mutation<User, UserId>({
      query: (id) => ({
        method: "PUT",
        url: `/api/user/${id}/reactivate`,
      }),
      invalidatesTags: (_, error, id) =>
        invalidateTags(error, [
          listTag("user"),
          idTag("user", id),
          listTag("permissions-group"),
        ]),
    }),
    updateUser: builder.mutation<User, UpdateUserRequest>({
      query: ({ id, ...body }) => ({
        method: "PUT",
        url: `/api/user/${id}`,
        body,
      }),
      invalidatesTags: (_, error, { id }) =>
        invalidateTags(error, [
          listTag("user"),
          idTag("user", id),
          idTag("current-user", id),
        ]),
    }),
    getPasswordResetUrl: builder.mutation<
      { password_reset_url: string },
      UserId
    >({
      query: (id) => ({
        method: "POST",
        url: `/api/user/${id}/password-reset-url`,
      }),
    }),
    listUserAttributes: builder.query<string[], void>({
      query: () => "/api/mt/user/attributes",
      providesTags: (response) => (response ? [listTag("user")] : []),
    }),
    updateUserModalQbnewb: builder.mutation<void, UserId>({
      query: (id) => ({
        method: "PUT",
        url: `/api/user/${id}/modal/qbnewb`,
      }),
      invalidatesTags: (_, error, id) =>
        invalidateTags(error, [idTag("user", id), idTag("current-user", id)]),
    }),
  }),
});

export const loadCurrentUser = () =>
  userApi.endpoints.getCurrentUser.initiate();

export const refetchCurrentUser = () =>
  userApi.endpoints.getCurrentUser.initiate(undefined, { forceRefetch: true });

export const {
  useListUsersQuery,
  useListUserRecipientsQuery,
  useGetUserQuery,
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useCreateUserMutation,
  useUpdatePasswordMutation,
  useDeactivateUserMutation,
  useReactivateUserMutation,
  useUpdateUserMutation,
  useGetPasswordResetUrlMutation,
  useListUserAttributesQuery,
  useUpdateUserModalQbnewbMutation,
} = userApi;
